/**
 * Recording types of an AI scribe. The value is stored as the scribe's
 * `scribeType`; the label is what users see. Note/Dictation keeps its stored
 * value and is shown as "Live Transcription".
 */
export const RECORDING_TYPE = {
  SESSION: 'Session',
  LIVE_TRANSCRIPTION: 'Note/Dictation',
  MEASUREMENT: 'Measurement',
} as const;

export type RecordingType =
  (typeof RECORDING_TYPE)[keyof typeof RECORDING_TYPE];

export type RecordingTypeOption = { value: RecordingType; label: string };

const RECORDING_TYPE_LABELS: Record<RecordingType, string> = {
  Session: 'Session',
  'Note/Dictation': 'Live Transcription',
  Measurement: 'Measurement',
};

const toOption = (value: RecordingType): RecordingTypeOption => ({
  value,
  label: RECORDING_TYPE_LABELS[value],
});

/** Offered for new scribes. Measurement is only kept for drafts that have it. */
const OFFERED_OPTIONS: RecordingTypeOption[] = [
  RECORDING_TYPE.SESSION,
  RECORDING_TYPE.LIVE_TRANSCRIPTION,
].map(toOption);

const OPTIONS_WITH_MEASUREMENT: RecordingTypeOption[] = [
  ...OFFERED_OPTIONS,
  toOption(RECORDING_TYPE.MEASUREMENT),
];

/** Label for a stored `scribeType`; unknown values are shown as stored. */
export function recordingTypeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return RECORDING_TYPE_LABELS[value as RecordingType] ?? value;
}

/**
 * Options for the recording type dropdown. A value the form already holds that
 * is no longer offered (a restored Measurement draft) is kept so the field
 * isn't blank.
 */
export function recordingTypeOptions(
  current: string | null | undefined,
): RecordingTypeOption[] {
  return current === RECORDING_TYPE.MEASUREMENT
    ? OPTIONS_WITH_MEASUREMENT
    : OFFERED_OPTIONS;
}

/**
 * Recording type preselected when the AI scribe modal starts blank. Mirrors
 * `AI_SCRIBE_DEFAULT_RECORDING_TYPES` in document-llm-api, which owns the values.
 */
export const AI_SCRIBE_DEFAULT_RECORDING_TYPES = [
  'SESSION',
  'LIVE_TRANSCRIPTION',
  'NONE',
] as const;

export type AiScribeDefaultRecordingType =
  (typeof AI_SCRIBE_DEFAULT_RECORDING_TYPES)[number];

export const DEFAULT_AI_SCRIBE_DEFAULT_RECORDING_TYPE: AiScribeDefaultRecordingType =
  'NONE';

export function isAiScribeDefaultRecordingType(
  value: unknown,
): value is AiScribeDefaultRecordingType {
  return (
    typeof value === 'string' &&
    (AI_SCRIBE_DEFAULT_RECORDING_TYPES as readonly string[]).includes(value)
  );
}

/** The recording type to preselect, or null for nothing. */
export function defaultRecordingTypeValue(
  setting: AiScribeDefaultRecordingType,
): RecordingType | null {
  switch (setting) {
    case 'SESSION':
      return RECORDING_TYPE.SESSION;
    case 'LIVE_TRANSCRIPTION':
      return RECORDING_TYPE.LIVE_TRANSCRIPTION;
    case 'NONE':
      return null;
  }
}
